import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Chip,
  Button,
  LinearProgress,
  Divider,
} from "@mui/material";
import { useConfigDataService } from "../../services/ConfigDataService";
import AdminUserFormDialog from "./AdminUserFormDialog"; // adjust path as needed
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";

const columnDefs = [
  { key: "Full_Name", label: "Full Name" },
  { key: "MailiD", label: "Email" },
  { key: "IsAuthorized", label: "Web Login" },
  { key: "IsSuperAdmin", label: "Super Admin" },
  { key: "BusinessVertical", label: "Business Verticals" },
  { key: "Project_Location", label: "Project Locations" },
];

const AdminUserAccessList = () => {
  const { fetchAdminUserList } = useConfigDataService();
  const [data, setData] = useState([]);
  const [search, setSearch] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAccessId, setSelectedAccessId] = useState(0); // 0 = create

  const handleDialogOpen = (accessId = 0) => {
    setSelectedAccessId(accessId); // pass 0 for create, or existing accessId for edit
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleFormSubmit = async (formData) => {
    console.log("Submit payload:", formData);
    await fetchData(); // refresh table
    // TODO: replace with your API call here
    // await adminUserService.save(formData);
    setDialogOpen(false);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetchAdminUserList();
      setData(response || []);
    } catch (err) {
      console.error("Error fetching admin user list", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredData = useMemo(() => {
    return data.filter((item) =>
      columnDefs.every(({ key }) => {
        const term = search[key]?.toLowerCase() || "";
        return item[key]?.toString().toLowerCase().includes(term);
      })
    );
  }, [data, search]);

  const renderChips = (value, type) => {
    if (!value) return "-";
    const parts = value.split(",").map((v) => v.trim());
    const allAreAll = parts.every((p) => p.toLowerCase() === "all");
    if (type === "location" && allAreAll) {
      return (
        <Chip
          label="All"
          size="small"
          sx={{ fontSize: "0.85rem", height: 22 }}
        />
      );
    }
    return parts.map((part, idx) => (
      <Chip
        key={idx}
        label={part}
        size="small"
        sx={{ fontSize: "0.85rem", height: 22, mr: 0.5, mb: 0.5 }}
      />
    ));
  };

  return (
    <Box sx={{ p: 1 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h2">Admin Access Users List</Typography>
        <Button variant="contained" onClick={() => handleDialogOpen(0)}>
          Create New User
        </Button>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {loading ? (
        <LinearProgress />
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: "70vh" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {columnDefs.map(({ key, label }) => (
                  <TableCell key={key}>
                    <TextField
                      label={label}
                      variant="standard"
                      fullWidth
                      value={search[key] || ""}
                      onChange={(e) =>
                        setSearch((prev) => ({
                          ...prev,
                          [key]: e.target.value,
                        }))
                      }
                    />
                  </TableCell>
                ))}
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((row, idx) => (
                <TableRow key={row.AccessId || idx}>
                  {columnDefs.map(({ key }) => (
                    <TableCell key={key} sx={{ fontSize: "0.85rem" }}>
                      {typeof row[key] === "boolean" ? (
                        <Chip
                          label={row[key] ? "Yes" : "No"}
                          size="small"
                          sx={{ fontSize: "0.85rem", height: 22 }}
                          color={row[key] ? "success" : "error"}
                        />
                      ) : key === "BusinessVertical" ? (
                        <Box display="flex" flexWrap="wrap">
                          {renderChips(row[key], "vertical")}
                        </Box>
                      ) : key === "Project_Location" ? (
                        <Box display="flex" flexWrap="wrap">
                          {renderChips(row[key], "location")}
                        </Box>
                      ) : (
                        row[key] || "-"
                      )}
                    </TableCell>
                  ))}
                  <TableCell>
                    <IconButton
                      onClick={() => handleDialogOpen(row.AccessId)}
                      size="small"
                      sx={{ color: "text.primary" }} // or 'white' or a theme color
                      title="Edit User"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AdminUserFormDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleFormSubmit}
        Access_id={selectedAccessId}
      />
    </Box>
  );
};

export default AdminUserAccessList;
